// PDF export pipeline. The four contractor-side prints (bill, quote, signed
// work order, signed-ticket archive) call this with a built jsPDF doc.
//
// Web:    download the PDF via a hidden anchor click.
// Native: write to the Cache directory, then open the native Share sheet so
//         the tech can text / email / save the PDF straight from the field.

import { Capacitor } from "@capacitor/core";

// Returns a data URL the jsPDF.addImage call can consume. Handles two cases:
//
//   - Already-base64 data URLs (signature captures from <canvas>): pass
//     through unchanged.
//   - Remote URLs (Firebase Storage logos / photos): load via an Image
//     element with crossOrigin="anonymous", then read pixels through a
//     canvas. This is more reliable than fetch + readAsDataURL because
//     Firebase's download URLs respond to image-element loads even when
//     a fetch() preflight gets blocked.
//
// Returns null on failure so the caller can skip the image without aborting.
export async function loadImageAsDataUrl(url) {
  if (!url) return null;
  if (typeof url === "string" && url.startsWith("data:")) return url;

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (err) {
        console.warn("loadImageAsDataUrl: canvas read failed:", err);
        resolve(null);
      }
    };
    img.onerror = (err) => {
      console.warn("loadImageAsDataUrl: image load failed:", url, err);
      resolve(null);
    };
    img.src = url;
  });
}

// Convert a jsPDF doc to a Blob.
function docToBlob(doc) {
  return doc.output("blob");
}

// Convert a jsPDF doc to a base64 string (no data URL prefix) for the native
// Filesystem.writeFile call.
function docToBase64(doc) {
  const dataUri = doc.output("datauristring");
  return dataUri.slice(dataUri.indexOf(",") + 1);
}

async function exportWeb(doc, filename) {
  const blob = docToBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function exportNative(doc, filename) {
  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  const { Share } = await import("@capacitor/share");

  const base64 = docToBase64(doc);
  const write = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });

  await Share.share({
    title: filename,
    url: write.uri,
    dialogTitle: "Share PDF",
  });
}

// Single entry point. Pass a built jsPDF doc and a filename (with .pdf).
export async function exportPdf(doc, filename) {
  if (!filename.toLowerCase().endsWith(".pdf")) {
    filename = `${filename}.pdf`;
  }
  if (Capacitor.getPlatform() === "web") {
    await exportWeb(doc, filename);
  } else {
    await exportNative(doc, filename);
  }
}

// Opens the PDF in a new tab with the auto-print action set, so the
// browser's PDF viewer pops the print dialog automatically. Used by the
// "Print" branch of the web chooser modal. Native devices never call
// this — Share sheet has its own Print destination.
export function printPdf(doc) {
  doc.autoPrint();
  const url = URL.createObjectURL(docToBlob(doc));
  const opened = window.open(url, "_blank");
  if (!opened) {
    // Popup blocker swallowed it — fall back to anchor click so the user
    // at least gets the PDF in a new tab.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Returns true if we should pop a chooser modal (web). On native the
// Share sheet is the chooser, so callers skip the modal and call exportPdf.
export function shouldShowPdfChooser() {
  return Capacitor.getPlatform() === "web";
}
